import { Button } from "@mui/material";
import { useMutation } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import {
	ModalNavigator,
	useModalNavigator,
	useModalRoute,
} from "../../../utils/components/ModalNavigator/ModalNavigator";
import LoadingIconButton from "../../../utils/components/buttons/LoadingIconButton";
import IconDialog from "../../../utils/components/dialogs/IconDialog";
import CertiblokIcon from "../../../utils/components/icons/CertiblokIcon";
import MLDialog from "../../../utils/components/poppers";
import { useBetterMediaQuery } from "../../../utils/hooks/useBetterMediaQuery";
import { apiErrorParser, useApi } from "../../../utils/hooks/api";
import { EditRoomApi } from "certiblok-api-manager";

type RestoreChangeModalProps = {
	changeId: string;
	type: "editRoom" | "document" | "folder";
	onRestore?: () => void;
};

const RestoreChangeModal = ModalNavigator.create<RestoreChangeModalProps>(({ changeId, type, onRestore }) => {
	const { t } = useTranslation();
	const { visible } = useModalRoute();
	const { popModal, dismissModal } = useModalNavigator();

	const isMd = useBetterMediaQuery("md");

	const editRoomApi = useApi(EditRoomApi);

	const restoreChangeMutation = useMutation({
		mutationFn: () => {
			return editRoomApi.resumeEditRoomUpdate(changeId);
		},
		onSuccess: () => {
			onRestore?.();
			dismissModal();
			MLDialog.showSnackbar(t("editRoom.changeRestoredSuccessfully"), { variant: "success" });
		},
		onError: (err) => {
			MLDialog.showSnackbar(apiErrorParser(err), { variant: "error" });
		},
	});

	const onClose = () => {
		if (restoreChangeMutation.isLoading) return;
		popModal();
	};

	return (
		<IconDialog
			open={visible}
			onClose={onClose}
			icon="comunication_outline_01"
			IconProps={{
				iconColor: "warning",
				containerClassName: "bg-[rgba(255,_248,_222,_1)]",
			}}
			DialogProps={{
				className: "flex flex-col items-stretch",
			}}
			title={t("editRoom.restoreChange")}
			description={
				type === "editRoom"
					? t("editRoom.restoreChangeDescriptionEditRoom")
					: type === "document"
					? t("editRoom.restoreChangeDescriptionDocument")
					: t("editRoom.restoreChangeDescriptionFolder")
			}
			ModalProps={{
				responsive: false,
				containerSx: {
					maxWidth: 440,
				},
			}}
			footer={
				<div className="flex items-center justify-between gap-2 md:pt-9">
					<Button
						disabled={restoreChangeMutation.isLoading}
						onClick={onClose}
						size={isMd ? "large" : "small"}
						color="secondary"
						children={
							<>
								<CertiblokIcon size={24} color="inherit" name={"close"} />
								{t("global.cancel")}
							</>
						}
					/>
					<LoadingIconButton
						loading={restoreChangeMutation.isLoading}
						onClick={() => {
							restoreChangeMutation.mutate();
						}}
						icon={<CertiblokIcon size={24} color="inherit" name={"delete_bold"} />}
						color="secondary"
						variant="contained"
						size={isMd ? "large" : "small"}
						children={<>{t("editRoom.restoreChange")}</>}
					/>
				</div>
			}
		/>
	);
});
export default RestoreChangeModal;
